import type { dash_notification_plat } from "@dropbox/api-v2-client";
import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";
import { getCachedOrFetchFeatureValue } from "@mirage/service-experimentation";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { tagged } from "@mirage/service-logging";
import type { LogoutServiceConsumerContract } from "@mirage/service-logout";
import Sentry from "@mirage/shared/sentry";
import * as jobs from "@mirage/shared/util/jobs";
import type { Observable } from "rxjs";
import { Subject } from "rxjs";
import { NotificationsBolt } from "../bolt";
import type { DashNotification } from "../data/notifications";
import {
  NotificationReadStatus,
  NotificationSeenState,
} from "../data/notifications";

import type { BluenoteSurfaces, DNPCategoryName } from "./api";
import {
  actOnBluenote,
  bluenoteRetrieve,
  dnpPreferenceGet,
  dnpPreferenceSet,
} from "./api";
export type Service = ReturnType<typeof notificationsService>;

type NotificationsServiceContract = {
  callApiV2: APIv2Callable;
  surface: BluenoteSurfaces;
  boltInitProps: {
    origin: string;
    isDev: boolean;
    isTest: boolean;
  } | null;
  logoutService: LogoutServiceConsumerContract;
  buildVersion: string;
};

type LoudNotificationData = {
  notifications: DashNotification[];
};

const logger = tagged("service-notifications");

const bluenoteSurfaceToPapSurface = (surface: BluenoteSurfaces) => {
  switch (surface) {
    case "dash-desktop":
      return {
        ".tag": "dash_surface_desktop" as const,
      };
    case "dash-web":
      return {
        ".tag": "dash_surface_web" as const,
      };
    case "dash-extension":
      return {
        ".tag": "dash_surface_extension" as const,
      };
    default:
      return undefined;
  }
};

const TWENTY_FOUR_HOURS_S = 60 * 60 * 24;
// Ported from FSS Desktop: https://dropbox.sourcegraphcloud.com/github.com/dropbox-internal/server/-/blob/desktop/dropbox/client/notifications/controller.py?L1211
export function shouldCreateLoudNotification(notification: DashNotification) {
  if (
    notification.status === NotificationReadStatus.READ ||
    notification.status === NotificationReadStatus.INVISIBLE ||
    notification.seen_state === NotificationSeenState.SEEN
  ) {
    return false;
  }

  if (
    !notification.parsedPayload.isPersistent &&
    Date.now() / 1000 - (notification.timestampSeconds ?? 0) >
      TWENTY_FOUR_HOURS_S
  ) {
    logger.info("Skipping non-persistent notification that is too old");
    return false;
  }
  return true;
}

export default function notificationsService({
  callApiV2,
  surface,
  boltInitProps,
  logoutService,
  buildVersion,
}: NotificationsServiceContract) {
  let mostRecentNotificationId: string | undefined;
  const bolt = boltInitProps
    ? new NotificationsBolt(
        boltInitProps.origin,
        boltInitProps.isDev,
        boltInitProps.isTest,
        () => refreshNotifications(),
      )
    : null;
  const loudNotificationSubject = new Subject<LoudNotificationData>();

  async function startPeriodicRefreshNotifications() {
    async function doRefresh() {
      return await refreshNotifications();
    }
    const EVERY_FIVE_MINUTES = 1000 * 60 * 5;
    // Intention is for this to always be on, but if a bug causes our backend to get hammered with request we want a way to turn this off
    const shouldStartJob = convertFeatureValueToBool(
      await getCachedOrFetchFeatureValue(
        "otc_growth_2025_06_26_auto_refresh_notifications",
      ),
    );
    const runOnStart = false;
    if (shouldStartJob) {
      jobs.register(
        "refreshNotifications",
        EVERY_FIVE_MINUTES,
        runOnStart,
        doRefresh,
      );
    }
  }

  function stopPeriodicRefreshNotifications() {
    jobs.unregister("refreshNotifications");
  }

  async function listInitialNotifications() {
    try {
      const { notifications, boltData } = await bluenoteRetrieve(
        callApiV2,
        surface,
      );
      const loudNotifications = notifications.filter(
        shouldCreateLoudNotification,
      );
      loudNotificationSubject.next({ notifications: loudNotifications });
      bolt?.subscribe(boltData);
      mostRecentNotificationId = notifications[0]?.nid;
    } catch (e) {
      logger.error("Failed to list notifications", e);
      throw e;
    }
  }

  async function refreshNotifications() {
    try {
      const { notifications, boltData } = await bluenoteRetrieve(
        callApiV2,
        surface,
        mostRecentNotificationId,
      );
      const loudNotifications = notifications.filter(
        shouldCreateLoudNotification,
      );
      loudNotificationSubject.next({ notifications: loudNotifications });
      bolt?.subscribe(boltData);
      mostRecentNotificationId = notifications[0]?.nid;
    } catch (e) {
      // Unlike the initial list, this is not triggered by the user so we don't rethrow
      logger.error("Failed to refresh notifications", e);
      Sentry.captureException(e, {
        data: {
          message: "Failed to refresh notifications",
        },
      });
    }
  }
  function loudNotificationObservable(): Observable<LoudNotificationData> {
    return loudNotificationSubject.asObservable();
  }

  // The backend marks these as seen and read, and if it's an ephemeral notification we remove it
  async function ackNotification(notification: DashNotification) {
    const args: dash_notification_plat.DNPBluenotePushActivityArg = {
      action_name: { ".tag": "dnp_notification_action_seen" },
      target_object_key: notification.parsedPayload.targetObjectKey,
      build_version: buildVersion,
      dash_surface: bluenoteSurfaceToPapSurface(surface),
      campaign_id: notification.parsedPayload.campaignId,
      nid: notification.nid,
    };
    try {
      await actOnBluenote(callApiV2, args);
    } catch (e) {
      // Don't rethrow because it's called automatically upon showing the notification - not user-triggered
      logger.error("Failed to ackBluenote", e);
      Sentry.captureException(e, {
        data: {
          message: "Failed to ackBluenote",
        },
      });
    }
  }

  async function markNotificationDismissed(
    notification: DashNotification,
    isAutoDismiss: boolean,
  ) {
    const args: dash_notification_plat.DNPBluenotePushActivityArg = {
      action_name: { ".tag": "dnp_notification_action_dismissed" },
      target_object_key: notification.parsedPayload.targetObjectKey,
      build_version: buildVersion,
      dash_surface: bluenoteSurfaceToPapSurface(surface),
      campaign_id: notification.parsedPayload.campaignId,
      is_auto_dismiss: isAutoDismiss,
      nid: notification.nid,
    };
    try {
      await actOnBluenote(callApiV2, args);
    } catch (e) {
      // This is just used for logging so don't surface any failures to the users
      logger.error("Failed to markNotificationDismissed", e);
      Sentry.captureException(e, {
        data: {
          message: "Failed to markNotificationDismissed",
        },
      });
    }
  }

  async function selectNotificationAction(
    notification: DashNotification,
    action: dash_notification_plat.ActionTargetId,
  ) {
    const args: dash_notification_plat.DNPBluenotePushActivityArg = {
      action_target_id: action,
      action_name: { ".tag": "dnp_notification_action_interacted" },
      target_object_key: notification.parsedPayload.targetObjectKey,
      build_version: buildVersion,
      dash_surface: bluenoteSurfaceToPapSurface(surface),
      campaign_id: notification.parsedPayload.campaignId,
      nid: notification.nid,
    };
    try {
      await actOnBluenote(callApiV2, args);
    } catch (e) {
      // This is just used for logging so don't surface any failures to the users
      logger.error("Failed to selectNotificationAction", e);
      Sentry.captureException(e, {
        data: {
          message: "Failed to selectNotificationAction",
        },
      });
    }
  }

  async function getAllDNPPreferences(): Promise<
    dash_notification_plat.DNPPreference[] | null
  > {
    try {
      return await dnpPreferenceGet(callApiV2);
    } catch (e) {
      const message = "Failed to fetch notification preferences";
      logger.error(message, e);
      Sentry.captureException(e, {
        data: {
          message,
        },
      });
      return null;
    }
  }

  async function setDNPPreference(
    categoryName: DNPCategoryName,
    isSubscribed: boolean,
  ): Promise<boolean> {
    try {
      return await dnpPreferenceSet(callApiV2, categoryName, isSubscribed);
    } catch (e) {
      const message = `Failed to set notification preference for ${categoryName} to ${isSubscribed}`;
      logger.error(message, e);
      Sentry.captureException(e, {
        data: {
          message,
        },
      });
      return false;
    }
  }

  // These are async by convention even though this doesn't do anything async
  async function tearDown() {
    bolt?.stop();
    stopPeriodicRefreshNotifications();
  }

  logoutService.registerLogoutCallback(ServiceId.NOTIFICATIONS, async () => {
    await tearDown();
  });

  return services.provide(
    ServiceId.NOTIFICATIONS,
    {
      listInitialNotifications,
      refreshNotifications,
      loudNotificationObservable,
      startPeriodicRefreshNotifications,
      stopPeriodicRefreshNotifications,
      ackNotification,
      markNotificationDismissed,
      selectNotificationAction,
      getAllDNPPreferences,
      setDNPPreference,
    },
    [ServiceId.DBX_API],
  );
}
